import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { getUser } from "../../api/auth";
import api from "../../api/axios";
import { useToast } from "../../context/ToastContext";

const MODULES = [
    "Dashboard",
    "Brands",
    "Templates",
    "Creatives",
    "Campaigns",
];

const createPermissions = () =>
    MODULES.map((module) => ({
        per_name: module,
        per_view: true,
        per_create: true,
        per_edit: true,
        per_delete: true,
    }));

export default function UserComponent({ role }) {
    const [search, setSearch] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({
        user_name: "",
        email: "",
        password: "",
        mobile: "",
        permissions: createPermissions(),
    });
    const [errors, setErrors] = useState({});
    const [isEdit, setIsEdit] = useState(false);
    const [editId, setEditId] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const { success, error } = useToast();

    const loadOrganizationUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get("/user/list?role=user");
            if (response?.data?.success) {
                setUsers(response.data.data.list || []);
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.log(err?.response?.data?.message || err.message);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOrganizationUsers();
    }, []);

    const filteredUsers = users.filter(
        (user) =>
            user.user_name.toLowerCase().includes(search.toLowerCase()) ||
            user.email.toLowerCase().includes(search.toLowerCase())
    );

    // Delete user (with API call)
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this user?")) return;
        try {
            await api.delete(`/user/${id}`);
            success("User deleted successfully");
            loadOrganizationUsers();
        } catch (err) {
            error(err?.response?.data?.message || "Failed to delete user");
        }
    };

    // Input change handler – clears field error
    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        // Clear error for this field
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    // Edit handler
    const handleEdit = (user) => {
        setIsEdit(true);
        setEditId(user.id);
        setFormData({
            id: user.id,
            user_name: user.user_name,
            email: user.email,
            password: "", // password field hidden on edit
            mobile: user.mobile || "",
            status: user?.status || "ACTIVE",
            permissions: user.permissions
                ? [...user.permissions]
                : createPermissions(),
        });
        setErrors({});
        setOpenModal(true);
    };

    // Permission toggle
    const handlePermission = (index, field) => {
        const permissions = [...formData.permissions];
        permissions[index][field] = !permissions[index][field];
        setFormData({
            ...formData,
            permissions,
        });
    };

    // Reset form and errors
    const resetForm = () => {
        setFormData({
            user_name: "",
            email: "",
            password: "",
            mobile: "",
            permissions: createPermissions(),
        });
        setErrors({});
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};

        // User name: required, min 2 chars
        if (!formData.user_name || formData.user_name.trim().length < 2) {
            newErrors.user_name = "User name is required (minimum 2 characters)";
        }

        // Email: required, valid email
        if (!formData.email) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }

        // Password: required only when creating, min 6 chars
        if (!isEdit) {
            if (!formData.password) {
                newErrors.password = "Password is required";
            } else if (formData.password.length < 6) {
                newErrors.password = "Password must be at least 6 characters";
            }
        }

        // Mobile: optional, but if provided must be a valid 10-digit number (basic)
        if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile)) {
            newErrors.mobile = "Mobile number must be 10 digits";
        }

        // Status: required
        if (!formData.status) {
            newErrors.status = "Status is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Save user (create or update)
    const handleSaveUser = async () => {
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            const userDetail = getUser();
            const payload = {
                ...formData,
                role: "user",
                org_name: userDetail?.user_name,
                org_id: userDetail?.org_id,
            };

            if (isEdit) {
                await api.put(`/user/${formData.id}`, payload);
                success("User updated successfully");
            } else {
                await api.post("/user/save", payload);
                success("User added successfully");
            }
            await loadOrganizationUsers();
            resetForm();
            setOpenModal(false);
        } catch (err) {
            console.log(err);
            error(
                err?.response?.data?.message ||
                err?.data?.message ||
                "An error occurred"
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen p-6">
            <div className="mx-auto max-w-7xl">
                {/* Header */}
                <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Users</h1>
                        <p className="mt-1 text-gray-500">Manage users and permissions.</p>
                    </div>
                    <button
                        onClick={() => {
                            resetForm();
                            setIsEdit(false);
                            setOpenModal(true);
                        }}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
                    >
                        <Plus size={18} />
                        Create User
                    </button>
                </div>

                {/* Search */}
                <div className="mb-6 rounded-xl bg-white p-5 shadow">
                    <input
                        type="text"
                        name="search"
                        placeholder="Search by name or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none"
                    />
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-xl bg-white shadow">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Mobile</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Status</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length > 0 ? (
                                    filteredUsers.map((user) => (
                                        <tr key={user.id} className="border-t hover:bg-gray-50">
                                            <td className="px-6 py-4 font-medium">{user.user_name}</td>
                                            <td className="px-6 py-4">{user.email}</td>
                                            <td className="px-6 py-4">{user.mobile}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span
                                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                                        user.status === "ACTIVE"
                                                            ? "bg-green-100 text-green-700"
                                                            : "bg-red-100 text-red-700"
                                                    }`}
                                                >
                                                    {user.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex justify-center gap-3">
                                                    <button
                                                        onClick={() => handleEdit(user)}
                                                        className="rounded-lg p-2 text-amber-600 transition hover:bg-amber-100"
                                                        title="Edit User"
                                                    >
                                                        <Pencil size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(user.id)}
                                                        className="rounded-lg p-2 text-red-600 transition hover:bg-red-100"
                                                        title="Delete User"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="py-10 text-center text-gray-500">
                                            No users found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {openModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5">
                    <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b px-8 py-5">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">
                                    {isEdit ? "Update User" : "Create User"}
                                </h2>
                            </div>
                            <button
                                onClick={() => {
                                    resetForm();
                                    setOpenModal(false);
                                }}
                                className="text-3xl text-gray-400 hover:text-red-500"
                            >
                                ×
                            </button>
                        </div>

                        {/* Body */}
                        <div className="max-h-[75vh] overflow-y-auto p-6">
                            {/* User Details */}
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="mb-2 block font-medium">
                                        User Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        name="user_name"
                                        value={formData.user_name}
                                        onChange={handleInput}
                                        className={`w-full rounded-lg border px-4 py-3 ${
                                            errors.user_name ? "border-red-500" : "border-gray-300"
                                        }`}
                                        placeholder="Enter user name"
                                    />
                                    {errors.user_name && (
                                        <p className="mt-1 text-sm text-red-500">{errors.user_name}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="mb-2 block font-medium">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInput}
                                        className={`w-full rounded-lg border px-4 py-3 ${
                                            errors.email ? "border-red-500" : "border-gray-300"
                                        }`}
                                        placeholder="Enter email"
                                    />
                                    {errors.email && (
                                        <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                                    )}
                                </div>

                                {!isEdit && (
                                    <div>
                                        <label className="mb-2 block font-medium">
                                            Password <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInput}
                                            className={`w-full rounded-lg border px-4 py-3 ${
                                                errors.password ? "border-red-500" : "border-gray-300"
                                            }`}
                                            placeholder="Password (min 6 chars)"
                                        />
                                        {errors.password && (
                                            <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label className="mb-2 block font-medium">Mobile</label>
                                    <input
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInput}
                                        className={`w-full rounded-lg border px-4 py-3 ${
                                            errors.mobile ? "border-red-500" : "border-gray-300"
                                        }`}
                                        placeholder="Mobile Number (10 digits)"
                                    />
                                    {errors.mobile && (
                                        <p className="mt-1 text-sm text-red-500">{errors.mobile}</p>
                                    )}
                                </div>
                            </div>

                            {/* Permissions */}
                            <div className="mt-8 mb-2">
                                <h3 className="mb-5 text-xl font-semibold">Module Permissions</h3>
                                <div className="overflow-hidden rounded-xl border">
                                    <table className="w-full">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-5 py-2 text-left">Module</th>
                                                <th className="px-5 py-2 text-center">View</th>
                                                <th className="px-5 py-2 text-center">Create</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.permissions.map((item, index) => (
                                                <tr key={item.per_name} className="border-t hover:bg-gray-50">
                                                    <td className="px-5 py-3 font-medium">{item.per_name}</td>
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.per_view}
                                                            onChange={() => handlePermission(index, "per_view")}
                                                            className="h-4 w-4 accent-blue-600"
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.per_create}
                                                            onChange={() => handlePermission(index, "per_create")}
                                                            className="h-4 w-4 accent-blue-600"
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Status */}
                            <div className="mt-6">
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInput}
                                    className={`w-full rounded-lg border px-4 py-3 ${
                                        errors.status ? "border-red-500" : "border-gray-300"
                                    } focus:border-blue-500 focus:outline-none`}
                                >
                                    <option value="ACTIVE">Active</option>
                                    <option value="INACTIVE">Inactive</option>
                                </select>
                                {errors.status && (
                                    <p className="mt-1 text-sm text-red-500">{errors.status}</p>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 border-t px-8 py-5">
                            <button
                                onClick={() => {
                                    resetForm();
                                    setOpenModal(false);
                                }}
                                className="rounded-lg border px-6 py-2 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveUser}
                                disabled={submitting}
                                className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                                {submitting
                                    ? "Saving..."
                                    : isEdit
                                    ? "Update User"
                                    : "Create User"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}